import React from 'react'
import styled, { keyframes } from 'styled-components'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import CardComponent from '../components/CardComponent'
import LineaSVG from '../assets/images/LineaCurva.svg'
import LineaMobileSVG from '../assets/images/LineaCurvaMobile.svg'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import { getLangFromPath, withLang } from '../utils/i18nRoutes'

const OwnServices = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const linea = isMobile ? LineaMobileSVG : LineaSVG
  const { t } = useTranslation()
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)

  const specializations = t('servicios.specializations', { returnObjects: true })
  const cards = t('servicios.cards', { returnObjects: true })
  const steps = t('servicios.methodology.steps', { returnObjects: true })

  return (
    <Container>
      <Header>
        <Title>{t('servicios.header.title')}</Title>
        <HeaderSubtitle>{t('servicios.header.subtitle')}</HeaderSubtitle>
      </Header>

      <SpecializationsLabel>
        {t('servicios.specializationsHeader.title')}
      </SpecializationsLabel>

      <SpecializationsPanel>
        <SpecializationsGrid>
          {specializations.map((item) => (
            <CardComponent
              key={item.slug}
              title={item.title}
              subtitle={item.text}
              tag={item.tag}
              to={withLang(`/servicios/${item.slug}`, lang)}
              compact
            />
          ))}
        </SpecializationsGrid>
      </SpecializationsPanel>

      <GeneralLabel>{t('servicios.generalLabel')}</GeneralLabel>

      <GeneralPanel>
        <CardsGrid>
          {cards.map((item, index) => (
            <CardWithAnimation
              key={item.title}
              index={index + 1}
              title={item.title}
              subtitle={item.subtitle}
              featured={index === cards.length - 1}
            />
          ))}
        </CardsGrid>
      </GeneralPanel>

      <MethodologySection>
        <MethodologyHeader>
          <MethodologyTitle>{t('servicios.methodology.title')}</MethodologyTitle>
          <MethodologyText>{t('servicios.methodology.text')}</MethodologyText>
        </MethodologyHeader>

        <Steps>
          {steps.map((step, index) => (
            <Step key={step.title}>
              <StepNumber>{index + 1}</StepNumber>
              <StepTitle>{step.title}</StepTitle>
              <StepText>{step.text}</StepText>
            </Step>
          ))}
        </Steps>
      </MethodologySection>

      <ImgLinea src={linea} alt="" />
    </Container>
  )
}

const CardWithAnimation = ({ index, title, subtitle, featured }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })

  return (
    <CardWrapper
      ref={ref}
      className={inView ? 'visible' : 'hidden'}
      $featured={featured}
    >
      <CardComponent index={index} title={title} subtitle={subtitle} />
    </CardWrapper>
  )
}

export default OwnServices

const Container = styled.div`
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  box-sizing: border-box;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 20px;
  }
`

const Header = styled.div`
  max-width: 700px;
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: ${sizes.mobile}px) {
    margin-bottom: 24px;
  }
`

const Title = styled.p`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 43.2px;
  font-size: 36px;
  font-family: ${({ theme }) => theme.fonts.heading};
  margin: 0 0 12px;

  @media (max-width: ${sizes.mobile}px) {
    line-height: 24px;
    font-size: 20px;
    font-family: ${({ theme }) => theme.fonts.headingAlt};
    margin: 40px 0 8px;
  }
`

const HeaderSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 17px;
  line-height: 1.5;
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 12px;
  }
`

const dropIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const SpecializationsLabel = styled.p`
  width: 100%;
  max-width: 1140px;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.accent};
  margin: 0 0 16px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 11px;
    margin: 0 0 10px;
  }
`

const SpecializationsPanel = styled.div`
  width: 100%;
  max-width: 1140px;
  box-sizing: border-box;
  background-color: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(169, 221, 191, 0.07)'
      : 'rgba(169, 221, 191, 0.18)'};
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 64px;
  animation: ${dropIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: ${sizes.mobile}px) {
    padding: 16px;
    border-radius: 14px;
    margin-bottom: 40px;
  }
`

const SpecializationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const GeneralLabel = styled.p`
  width: 100%;
  max-width: 1140px;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0 0 16px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 11px;
    margin: 0 0 10px;
  }
`

const GeneralPanel = styled.div`
  width: 100%;
  max-width: 1140px;
  box-sizing: border-box;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 20px;
  padding: 24px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 16px;
    border-radius: 14px;
  }
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const CardWrapper = styled.div`
  grid-column: ${({ $featured }) => ($featured ? '1 / -1' : 'auto')};
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s ease-in-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`

const MethodologySection = styled.div`
  width: 100%;
  max-width: 1140px;
  margin-top: 96px;
  display: flex;
  gap: 64px;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
    gap: 32px;
    margin-top: 56px;
  }
`

const MethodologyHeader = styled.div`
  flex: 0 0 320px;

  @media (max-width: ${sizes.mobile}px) {
    flex: none;
  }
`

const MethodologyTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 28px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 20px;
  }
`

const MethodologyText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 13px;
  }
`

const Steps = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const Step = styled.div``

const StepNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 999px;
  margin: 0 0 12px;
`

const StepTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 15px;
  }
`

const StepText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 12px;
  }
`

const ImgLinea = styled.img`
  width: 100vw;
  max-width: 100vw;
  height: auto;
  margin: 80px 0 0 calc(-50vw + 50%);

  @media (max-width: ${sizes.mobile}px) {
    margin: 40px 0 0 calc(-50vw + 50%);
  }
`
